/**
 * ForgotPasswordPage — self-service password reset request.
 * Posts /api/auth/password-reset and always shows a "check your email"
 * confirmation regardless of whether the account exists (no enumeration).
 */
import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Mail, ShieldCheck } from 'lucide-react';
import BrandMark from '../components/BrandMark';

const ForgotPasswordPage = () => {
  const { API } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset`, { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      // Endpoint always returns 200 — show generic success even on network hiccup.
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="forgot-password-page">
      <header className="border-b border-border">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/login" className="inline-flex items-center gap-2 text-sm font-semibold text-foreground/80 hover:text-foreground" data-testid="fp-back-to-login">
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>
          <BrandMark />
          <div className="w-8" />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-md w-full space-y-6">
          <div className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Mail className="w-6 h-6 text-primary" />
            </div>
            <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground">Reset your password</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Enter your email and we'll send you a single-use link to choose a new password. The link expires in 30 minutes.
            </p>
          </div>

          {!sent ? (
            <form onSubmit={submit} className="space-y-3" data-testid="fp-form">
              <div className="space-y-1.5">
                <Label htmlFor="fp-email" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</Label>
                <Input
                  id="fp-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="h-11"
                  data-testid="fp-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                size="lg"
                className="w-full h-12 rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="fp-submit-btn"
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </Button>
            </form>
          ) : (
            <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-5 text-center" data-testid="fp-sent-state">
              <div className="mx-auto w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-emerald-700" />
              </div>
              <h2 className="mt-2 font-bold text-emerald-900">Check your email</h2>
              <p className="mt-1 text-sm text-emerald-800">
                If an account exists for <strong>{email}</strong>, we've sent you a reset link. Check your inbox and spam folder.
              </p>
              <Link
                to="/login"
                className="mt-4 inline-block text-sm font-bold text-primary hover:underline"
                data-testid="fp-back-link"
              >
                Back to sign in →
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ForgotPasswordPage;
